import { useMemo } from 'react'

import { useSplitEnabled } from '@buffer-mono/features'

import {
  parsePostTypes,
  POST_TYPE_FILTER_SPLIT,
  supportedPostTypesForService,
} from '~publish/helpers/postType'
import type { FilterablePostType } from '~publish/helpers/postType'
import { useQueryParam } from '~publish/hooks/useQueryParam'

const EMPTY_POST_TYPES: FilterablePostType[] = []

export type PostTypeFilterState = {
  /** Whether to render the control at all. */
  isEnabled: boolean
  /** Formats this channel's service can be filtered by, in display order. */
  options: readonly FilterablePostType[]
  selected: FilterablePostType[]
  setSelected: (value: string[] | undefined) => void
}

/**
 * Owns a page's post format selection: the flag, the `postTypes` URL
 * param, and the narrowing to what the channel's service supports.
 *
 * The selection is gated, not only the control. A shared URL carries
 * `postTypes`, and neither a flagged-off session nor an unsupported service
 * renders a control to clear it with, so an ungated read would filter the page
 * with nothing on screen to explain why.
 */
export function usePostTypeFilter(
  service: string | null | undefined,
): PostTypeFilterState {
  const { isEnabled: isSplitEnabled } = useSplitEnabled(POST_TYPE_FILTER_SPLIT)
  const [queryPostTypes, setSelected] = useQueryParam<string[]>('postTypes')

  const options = useMemo(
    () => (isSplitEnabled ? supportedPostTypesForService(service) : []),
    [isSplitEnabled, service],
  )

  const selected = useMemo(() => {
    if (options.length === 0) return EMPTY_POST_TYPES
    const supported: readonly string[] = options
    return parsePostTypes(queryPostTypes).filter((postType) =>
      supported.includes(postType),
    )
  }, [options, queryPostTypes])

  return {
    isEnabled: options.length > 0,
    options,
    selected,
    setSelected,
  }
}
